import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { SOCIAL_MEDIA_CONFIG, buildFacebookOAuthUrl, logOAuthConfig } from '../../config/socialMediaConfig';
import './FacebookOAuth.css';

interface FacebookOAuthProps {
  storeId: string;
  storeName: string;
  onConnectionSuccess?: (accountData: any) => void;
  onConnectionError?: (error: string) => void;
}

interface FacebookAccount {
  accountId: string;
  platform: string;
  accountName: string;
  pageId: string;
  isActive: boolean;
  connectedAt: string;
  fanCount?: number;
  category?: string;
}

export const FacebookOAuth: React.FC<FacebookOAuthProps> = ({
  storeId,
  storeName,
  onConnectionSuccess,
  onConnectionError
}) => {
  const { getIdToken } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<FacebookAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { redirectUri: REDIRECT_URI } = SOCIAL_MEDIA_CONFIG.facebook;

  useEffect(() => {
    loadConnectedAccounts();
    logOAuthConfig(); // Debug logging
  }, [storeId]);

  const loadConnectedAccounts = async () => {
    try {
      setIsLoading(true);
      const token = await getIdToken();
      
      const response = await fetch(
        `${API_ENDPOINTS.socialMediaAccounts()}?storeId=${storeId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        const facebookAccounts = result.data?.filter((acc: any) => acc.platform === 'facebook') || [];
        setConnectedAccounts(facebookAccounts);
      } else {
        console.error('Failed to load connected accounts');
      }
    } catch (error) {
      console.error('Error loading connected accounts:', error);
      setError('Failed to load connected accounts');
    } finally {
      setIsLoading(false);
    }
  };

  const initiateOAuthFlow = () => {
    setIsConnecting(true);
    setError(null);

    // Store state information for the callback
    const oauthState = JSON.stringify({
      storeId,
      storeName,
      timestamp: Date.now()
    });

    localStorage.setItem('facebook_oauth_state', oauthState);

    // Use the centralized OAuth URL builder
    const oauthUrl = buildFacebookOAuthUrl(storeId, storeName);
    
    console.log('Initiating Facebook OAuth:', {
      redirectUri: REDIRECT_URI,
      oauthUrl: oauthUrl.substring(0, 100) + '...' // Log truncated URL for security
    });

    // Redirect to Facebook
    window.location.href = oauthUrl;
  };

  const disconnectAccount = async (accountId: string) => {
    try {
      const token = await getIdToken();
      
      const response = await fetch(
        API_ENDPOINTS.socialMediaAccount(accountId),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setConnectedAccounts(prev => prev.filter(acc => acc.accountId !== accountId));
      } else {
        setError('Failed to disconnect account');
      }
    } catch (error) {
      console.error('Error disconnecting account:', error);
      setError('Failed to disconnect account');
    }
  };

  const syncFacebookPosts = async (account: FacebookAccount) => {
    try {
      setError(null);
      const token = await getIdToken();
      
      // For this demo, we'll use the page ID as both pageId and pageAccessToken
      // In a real implementation, you'd have the actual page access token stored
      const response = await fetch(API_ENDPOINTS.facebookSyncPosts(account.pageId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pageAccessToken: 'your-page-access-token', // TODO: Use actual stored token
          storeId: storeId,
          limit: 25
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(`Successfully synced ${result.data.synced} Facebook posts! ${result.data.skipped} posts were skipped (already exist).`);
      } else {
        setError(result.error || 'Failed to sync Facebook posts');
      }
    } catch (error) {
      console.error('Error syncing Facebook posts:', error);
      setError('Failed to sync Facebook posts');
    }
  };

  if (isLoading) {
    return (
      <div className="facebook-oauth">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading Facebook connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="facebook-oauth">
      <div className="oauth-header">
        <div className="platform-icon">Facebook</div>
        <div className="platform-info">
          <h3>Facebook Integration</h3>
          <p>Connect your restaurant's Facebook page to automatically post updates, promotions, and engage with customers.</p>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">[ERROR]</span>
          {error}
        </div>
      )}

      <div className="store-info">
        <h4>Store: {storeName}</h4>
        <p className="store-id">Store ID: {storeId}</p>
      </div>

      <div className="connection-status">
        {connectedAccounts.length === 0 ? (
          <div className="not-connected">
            <div className="status-icon">[CONNECT]</div>
            <h4>No Facebook Account Connected</h4>
            <p>Connect your restaurant's Facebook page to start automating your social media marketing.</p>
            
            <div className="permissions-info">
              <h5>Permissions Required:</h5>
              <ul>
                <li>Manage and publish posts to your Facebook page</li>
                <li>Read page insights and engagement metrics</li>
                <li>Access page information and fan counts</li>
                <li>Manage business account settings</li>
              </ul>
            </div>

            <button 
              onClick={initiateOAuthFlow}
              disabled={isConnecting}
              className="connect-facebook-btn"
            >
              {isConnecting ? (
                <>
                  <div className="btn-spinner"></div>
                  Connecting to Facebook...
                </>
              ) : (
                <>
                  <span className="facebook-icon">Facebook</span>
                  Connect Facebook Account
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="connected-accounts">
            <div className="status-icon">[CONNECTED]</div>
            <h4>Connected Facebook Accounts</h4>
            
            {connectedAccounts.map((account) => (
              <div key={account.accountId} className="account-card">
                <div className="account-info">
                  <div className="account-avatar">Facebook</div>
                  <div className="account-details">
                    <h5>{account.accountName}</h5>
                    <p className="account-meta">
                      {account.category && <span className="category">{account.category}</span>}
                      {account.fanCount !== undefined && (
                        <span className="fan-count">{account.fanCount} followers</span>
                      )}
                    </p>
                    <p className="connection-date">
                      Connected {new Date(account.connectedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div className="account-actions">
                  <span className={`status-badge ${account.isActive ? 'active' : 'inactive'}`}>
                    {account.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <button 
                    onClick={() => syncFacebookPosts(account)}
                    className="sync-posts-btn"
                    title="Sync Existing Posts"
                  >
                    Sync Posts
                  </button>
                  <button 
                    onClick={() => disconnectAccount(account.accountId)}
                    className="disconnect-btn"
                    title="Disconnect Account"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ))}
            
            <button 
              onClick={initiateOAuthFlow}
              className="connect-additional-btn"
              disabled={isConnecting}
            >
              Connect Additional Facebook Account
            </button>
          </div>
        )}
      </div>

      <div className="oauth-footer">
        <div className="security-note">
          <span className="shield-icon">[SECURE]</span>
          <p>Your Facebook account credentials are secure. BigSystemOne only stores access tokens needed to post on your behalf.</p>
        </div>
        
        <div className="help-links">
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <span className="separator">•</span>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          <span className="separator">•</span>
          <a href="#" onClick={(e) => { e.preventDefault(); /* TODO: Add help modal */ }}>
            Help & Support
          </a>
        </div>
      </div>
    </div>
  );
};